/**
 * ADR-0232 — Trust engine slice 2 (verification hardening) section of the
 * ops2026 Trust engine (shadow) panel: forward-confirmed reverse DNS
 * results and DNS budget use, Web Bot Auth signature strength mix,
 * replays refused, reused signatures, pinned key status and key-change
 * alerts, plus the per-reason backtest contribution. Read-only.
 */

import type { ReactNode } from 'react';

import type { ReasonContribution, RdnsResult } from '@sill/types';
import type { TrustHardeningSummary } from '@sill/types/api';

import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { formatCount } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { formatRate, share } from './trust-helpers.js';
import { KeyAlertsSection } from './KeyAlertsSection.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export const RDNS_LABEL: Readonly<Record<RdnsResult | 'pending', string>> = {
  confirmed: 'Confirmed',
  mismatch: 'Mismatch',
  no_ptr: 'No PTR',
  unknown: 'Unknown',
  ineligible: 'Ineligible',
  pending: 'Pending',
};
const RDNS_ORDER: readonly (RdnsResult | 'pending')[] = ['confirmed', 'mismatch', 'no_ptr', 'unknown', 'pending', 'ineligible'];

function useStyles() {
  const C = useC();
  return {
    C,
    kicker: { fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' as const, marginBottom: 8 },
    th: { textAlign: 'left' as const, fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' as const, color: C.muted, padding: '6px 8px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' as const },
    td: { padding: '6px 8px', color: C.body, borderBottom: `1px solid ${C.border}` },
  };
}

function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: string }): JSX.Element {
  const C = useC();
  return (
    <div style={{ minWidth: 130 }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: C.muted }}>{label}</div>
      <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text }}>{value}</div>
      {sub !== undefined ? <div style={{ fontFamily: sansFont, fontSize: 11.5, color: C.muted }}>{sub}</div> : null}
    </div>
  );
}

export function HardeningPanel({ h, onAck, includeAcked = false, onToggleAcked }: {
  h: TrustHardeningSummary;
  /** ADR-0236 — acknowledge a key-change alert (absent → read-only). */
  onAck?: (alertId: number, note: string) => Promise<void>;
  includeAcked?: boolean;
  onToggleAcked?: (next: boolean) => void;
}): JSX.Element {
  const { C, kicker } = useStyles();
  const rdnsTotal = RDNS_ORDER.reduce((a, k) => a + h.rdns.by_result[k], 0);
  const verifiedSigs = h.signatures.strong + h.signatures.weak;
  return (
    <>
      <div style={{ ...kicker, marginTop: 24 }}>Reverse DNS · last {h.rdns.window_days} days</div>
      <Card style={{ padding: 16 }} data-testid="hardening-rdns">
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {RDNS_ORDER.map((k) => (
            <div key={k} data-testid={`rdns-${k}`}>
              <Stat label={RDNS_LABEL[k]} value={formatCount(h.rdns.by_result[k])} sub={formatRate(share(h.rdns.by_result[k], rdnsTotal))} />
            </div>
          ))}
        </div>
        <div style={{ ...kicker, marginTop: 14 }}>DNS budget · last hour</div>
        <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.body }}>
          {formatCount(h.rdns.budget_used_last_hour)} queries · peak {formatCount(h.rdns.budget_peak_minute)} / {formatCount(h.rdns.budget_per_min)} per minute
          {h.rdns.budget_minutes_exhausted > 0 ? (
            <> · <Pill tone="amber">exhausted {h.rdns.budget_minutes_exhausted} min</Pill></>
          ) : null}
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 6 }}>
          Only Cloudflare-observed addresses on signed reports are looked up. Unknown, no PTR and budget misses never count against an agent; a mismatch is evidence only.
        </div>
      </Card>

      <div style={{ ...kicker, marginTop: 24 }}>Signatures · last {h.signatures.window_days} days</div>
      <Card style={{ padding: 16 }} data-testid="hardening-signatures">
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <Stat label="Presented" value={formatCount(h.signatures.presented)} />
          <div data-testid="sig-strong"><Stat label="Strong (≤ 5 min)" value={formatCount(h.signatures.strong)} sub={formatRate(share(h.signatures.strong, verifiedSigs))} /></div>
          <Stat label="Weak (longer window)" value={formatCount(h.signatures.weak)} sub={formatRate(share(h.signatures.weak, verifiedSigs))} />
          <Stat label="User-Agent covered" value={formatCount(h.signatures.ua_covered)} />
          <Stat label="No nonce" value={formatCount(h.signatures.no_nonce)} />
          <div data-testid="sig-replays"><Stat label="Replays refused" value={formatCount(h.signatures.replays_refused)} /></div>
          <Stat label="Reused (24 h)" value={formatCount(h.signatures.reused_signatures_24h)} sub="> 1 site or > 3 networks" />
        </div>
        <div style={{ ...kicker, marginTop: 14 }}>Outcomes</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(h.signatures.outcomes).length === 0 ? (
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>No signed requests yet.</span>
          ) : Object.entries(h.signatures.outcomes).sort((a, b) => b[1] - a[1]).map(([o, n]) => (
            <Pill key={o} tone={o === 'verified' ? 'lime' : o === 'invalid' ? 'coral' : 'neutral'}>{o}: {formatCount(n)}</Pill>
          ))}
        </div>
        <div style={{ ...kicker, marginTop: 14 }}>Pinned keys · hold {h.key_pin_hold_hours} h for new keys of bundled signers</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Pill tone="sky">{formatCount(h.pinned_keys)} keys · {formatCount(h.pinned_signers)} signers</Pill>
          <Pill tone="lime">trusted: {formatCount(h.signatures.key_status.trusted)}</Pill>
          <Pill tone={h.signatures.key_status.pending > 0 ? 'amber' : 'neutral'}>pending: {formatCount(h.signatures.key_status.pending)}</Pill>
          <Pill tone="neutral">unknown: {formatCount(h.signatures.key_status.unknown)}</Pill>
        </div>
      </Card>

      <KeyAlertsSection h={h} {...(onAck !== undefined ? { onAck } : {})} includeAcked={includeAcked} {...(onToggleAcked !== undefined ? { onToggleAcked } : {})} />
    </>
  );
}

/** Per-reason contribution of a backtest run (ADR-0232). */
export function ReasonContributionTable({ rc }: { rc: ReasonContribution }): JSX.Element {
  const { C, kicker, td } = useStyles();
  const rows = (m: Readonly<Record<string, number>>) => Object.entries(m).sort((a, b) => b[1] - a[1]);
  const block = (title: string, m: Readonly<Record<string, number>>, empty: string): JSX.Element => (
    <div style={{ minWidth: 220 }}>
      <div style={{ ...kicker, marginTop: 14 }}>{title}</div>
      {rows(m).length === 0 ? (
        <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted }}>{empty}</div>
      ) : (
        <table style={{ borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
          <tbody>
            {rows(m).map(([r, n]) => (
              <tr key={r}>
                <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5 }}>{r}</td>
                <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5, textAlign: 'right' }}>{formatCount(n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }} data-testid="reason-contribution">
      {block('Verified by', rc.verified_by, 'Nothing verified.')}
      {block('False accusations from', rc.false_accusation_reasons, 'None.')}
      {block('Suspicious flags from', rc.suspicious_reasons, 'None.')}
    </div>
  );
}
